import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { useApiError } from '../lib/useApiError';
import { useDeleteCustomer } from '../lib/queries';
import { Alert, Button, Card, Field, SectionTitle } from './ui';

/**
 * Permanent deletion of a customer.
 *
 * Everything else here is reversible or append-only — orders are refunded
 * rather than erased, balances move by ledger entry. This is the one door out,
 * so it is deliberately slow: it opens behind a button, states exactly what
 * goes, and will not proceed until the card number is typed out. A misplaced
 * click cannot reach the end of it.
 */
export function CustomerDelete({ customerId, loyaltyId, name }: {
  customerId: string;
  loyaltyId: string;
  name: string;
}) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const remove = useDeleteCustomer(customerId, () => navigate('/customers', { replace: true }));

  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');

  const errorText = useApiError();
  const error = remove.error instanceof ApiError ? remove.error : null;
  const matches = typed.trim().toUpperCase() === loyaltyId.toUpperCase();

  if (!open) {
    return (
      <Card>
        <SectionTitle>{t('delete.title')}</SectionTitle>
        <p className="text-sm leading-relaxed text-ink-dim">{t('delete.intro')}</p>
        <Button variant="ghost" className="mt-3 w-full" onClick={() => setOpen(true)}>
          {t('delete.open')}
        </Button>
      </Card>
    );
  }

  return (
    <Card>
      <SectionTitle>{t('delete.title')}</SectionTitle>

      {error && <div className="mb-3"><Alert>{errorText(error) ?? error.message}</Alert></div>}

      <div className="rounded-xl border border-danger/35 bg-danger/10 p-4">
        <p className="text-sm font-semibold text-ink">{t('delete.confirmQuestion', { name })}</p>
        <ul className="mt-2 space-y-1 text-xs text-ink-dim">
          <li>· {t('delete.bullet1')}</li>
          <li>· {t('delete.bullet2')}</li>
          <li>· {t('delete.bullet3')}</li>
          <li>· {t('delete.bullet4')}</li>
        </ul>

        <div className="mt-4">
          <Field
            label={t('delete.typeCard', { card: loyaltyId })}
            autoComplete="off"
            className="tabular uppercase"
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
          />
        </div>

        <div className="mt-3 flex gap-2">
          <Button
            variant="danger"
            className="flex-1"
            disabled={!matches}
            loading={remove.isPending}
            onClick={() => remove.mutate(typed.trim())}
          >
            {t('delete.confirm')}
          </Button>
          <Button
            variant="ghost"
            onClick={() => { setOpen(false); setTyped(''); remove.reset(); }}
          >
            {t('common.cancel')}
          </Button>
        </div>
      </div>
    </Card>
  );
}
